// ClaudeCode: Entry detail — view, decrypt, copy, share, edit, delete
import { useState, useEffect } from 'react';
import { getEntry, deleteEntry, updateEntry, createShareLink } from '../api';
import type { EntryFull } from '../api';
import { decrypt, encrypt, generateShareKey, copyToClipboard } from '../crypto';

const TIER_LABELS: Record<number, string> = {
  4: 'Trivial',
  3: 'Convenience',
  2: 'Sensitive',
  1: 'Irreplaceable',
};

interface EntryDetailProps {
  entryId: string;
  cryptoKey: CryptoKey;
  onBack: () => void;
  onDeleted: () => void;
}

export function EntryDetail({
  entryId,
  cryptoKey,
  onBack,
  onDeleted,
}: EntryDetailProps) {
  const [entry, setEntry] = useState<EntryFull | null>(null);
  const [decryptedValue, setDecryptedValue] = useState<string | null>(null);
  const [decryptedNotes, setDecryptedNotes] = useState<string | null>(null);
  const [isRevealed, setIsRevealed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Edit state
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editValue, setEditValue] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editTier, setEditTier] = useState(4);
  const [editCategory, setEditCategory] = useState('');
  const [editUrl, setEditUrl] = useState('');
  const [editTags, setEditTags] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getEntry(entryId)
      .then(setEntry)
      .catch((err) => setError(err.message));
  }, [entryId]);

  // Decrypt value on demand
  const handleReveal = async () => {
    if (!entry) return;
    try {
      const value = await decrypt(entry.encryptedValue, cryptoKey);
      setDecryptedValue(value);
      if (entry.encryptedNotes) {
        const notes = await decrypt(entry.encryptedNotes, cryptoKey);
        setDecryptedNotes(notes);
      }
      setIsRevealed(true);
    } catch {
      setError('Decryption failed — wrong passphrase?');
    }
  };

  // Enter edit mode — decrypt first if not already revealed
  const handleStartEdit = async () => {
    if (!entry) return;
    try {
      const value = decryptedValue ?? await decrypt(entry.encryptedValue, cryptoKey);
      const notes = entry.encryptedNotes
        ? (decryptedNotes ?? await decrypt(entry.encryptedNotes, cryptoKey))
        : '';

      setEditTitle(entry.title);
      setEditValue(value);
      setEditNotes(notes);
      setEditTier(entry.tier);
      setEditCategory(entry.category ?? '');
      setEditUrl(entry.url ?? '');
      // Fetch tags from the entry (they come from the GET response)
      setEditTags((entry as any).tags?.join(', ') ?? '');
      setIsEditing(true);
    } catch {
      setError('Decryption failed — cannot edit');
    }
  };

  // Save edits — re-encrypt and update
  const handleSaveEdit = async () => {
    if (!entry) return;
    setSaving(true);
    setError('');
    try {
      const encryptedValue = await encrypt(editValue, cryptoKey);
      const encryptedNotes = editNotes.trim()
        ? await encrypt(editNotes, cryptoKey)
        : undefined;

      const tags = editTags
        .split(',')
        .map((t) => t.trim().toLowerCase())
        .filter(Boolean);

      await updateEntry(entryId, {
        title: editTitle.trim(),
        encryptedValue,
        encryptedNotes,
        tier: editTier,
        category: editCategory.trim() || undefined,
        url: editUrl.trim() || undefined,
        tags,
      });

      // Reload entry
      const updated = await getEntry(entryId);
      setEntry(updated);
      setIsEditing(false);
      setIsRevealed(false);
      setDecryptedValue(null);
      setDecryptedNotes(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // Copy to clipboard with auto-clear
  const handleCopy = async () => {
    if (!decryptedValue && entry) {
      try {
        const value = await decrypt(entry.encryptedValue, cryptoKey);
        copyToClipboard(value);
      } catch {
        setError('Decryption failed');
        return;
      }
    } else if (decryptedValue) {
      copyToClipboard(decryptedValue);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Create ephemeral share link
  const handleShare = async () => {
    if (!entry) return;
    try {
      const plaintext = await decrypt(entry.encryptedValue, cryptoKey);
      const { key: shareKey, keyHex } = await generateShareKey();
      const encryptedPayload = await encrypt(plaintext, shareKey);
      const link = await createShareLink({
        encryptedPayload,
        label: entry.title,
      });
      const baseUrl = window.location.origin;
      setShareUrl(`${baseUrl}/share/${link.id}#${keyHex}`);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create share link');
    }
  };

  // Delete
  const handleDelete = async () => {
    try {
      await deleteEntry(entryId);
      onDeleted();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    }
  };

  if (error && !entry) {
    return (
      <div style={{ padding: 24 }}>
        <button onClick={onBack} style={backBtnStyle}>Back</button>
        <div style={{ color: '#f87171', marginTop: 12 }}>{error}</div>
      </div>
    );
  }

  if (!entry) {
    return <div style={{ padding: 24, color: '#71717a' }}>Loading...</div>;
  }

  // ── Edit mode ──
  if (isEditing) {
    return (
      <div style={{ padding: 24, maxWidth: 600 }}>
        <button
          onClick={() => setIsEditing(false)}
          style={backBtnStyle}
        >
          Cancel
        </button>

        <h2 style={{ fontSize: 20, fontWeight: 700, color: '#fafafa', margin: '16px 0 24px' }}>
          Edit Entry
        </h2>

        <label style={labelStyle}>Title</label>
        <input
          value={editTitle}
          onChange={(e) => setEditTitle(e.target.value)}
          style={inputStyle}
        />

        <label style={labelStyle}>Secret Value</label>
        <textarea
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          rows={3}
          style={{ ...inputStyle, resize: 'vertical', fontFamily: 'monospace' }}
        />

        <label style={labelStyle}>Notes (encrypted)</label>
        <textarea
          value={editNotes}
          onChange={(e) => setEditNotes(e.target.value)}
          rows={2}
          style={{ ...inputStyle, resize: 'vertical' }}
        />

        <label style={labelStyle}>Security Tier</label>
        <select
          value={editTier}
          onChange={(e) => setEditTier(parseInt(e.target.value, 10))}
          style={{ ...inputStyle, cursor: 'pointer' }}
        >
          <option value={4}>Tier 4 — Trivial</option>
          <option value={3}>Tier 3 — Convenience</option>
          <option value={2}>Tier 2 — Sensitive</option>
          <option value={1}>Tier 1 — Irreplaceable</option>
        </select>

        <label style={labelStyle}>Category</label>
        <input
          value={editCategory}
          onChange={(e) => setEditCategory(e.target.value)}
          style={inputStyle}
        />

        <label style={labelStyle}>URL / Domain</label>
        <input
          value={editUrl}
          onChange={(e) => setEditUrl(e.target.value)}
          style={inputStyle}
        />

        <label style={labelStyle}>Tags (comma-separated)</label>
        <input
          value={editTags}
          onChange={(e) => setEditTags(e.target.value)}
          style={inputStyle}
        />

        {error && (
          <div style={{ color: '#f87171', fontSize: 13, marginBottom: 12 }}>{error}</div>
        )}

        <button
          onClick={handleSaveEdit}
          disabled={saving || !editTitle.trim() || !editValue.trim()}
          style={{
            width: '100%',
            padding: '10px 0',
            background: saving ? '#27272a' : '#3b82f6',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            fontSize: 15,
            fontWeight: 600,
            cursor: saving ? 'wait' : 'pointer',
          }}
        >
          {saving ? 'Encrypting...' : 'Save Changes'}
        </button>
      </div>
    );
  }

  // ── View mode ──
  return (
    <div style={{ padding: 24, maxWidth: 600 }}>
      {/* Header */}
      <button onClick={onBack} style={backBtnStyle}>Back</button>

      <div style={{ marginTop: 16, marginBottom: 24 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: '#fafafa', margin: 0 }}>
          {entry.title}
        </h2>
        <div style={{ fontSize: 13, color: '#71717a', marginTop: 4, display: 'flex', gap: 12 }}>
          <span>Tier {entry.tier} — {TIER_LABELS[entry.tier]}</span>
          {entry.category && <span>{entry.category}</span>}
          {entry.url && <span>{entry.url}</span>}
        </div>
      </div>

      {/* Value section */}
      <div
        style={{
          background: '#09090b',
          border: '1px solid #27272a',
          borderRadius: 8,
          padding: 16,
          marginBottom: 16,
        }}
      >
        <label style={{ fontSize: 12, color: '#71717a', display: 'block', marginBottom: 8 }}>
          Secret Value
        </label>

        {isRevealed && decryptedValue ? (
          <pre
            style={{
              fontFamily: 'monospace',
              fontSize: 14,
              color: '#fafafa',
              wordBreak: 'break-all',
              whiteSpace: 'pre-wrap',
              margin: 0,
            }}
          >
            {decryptedValue}
          </pre>
        ) : (
          <div style={{ color: '#52525b', fontSize: 14, fontStyle: 'italic' }}>
            ******** (encrypted)
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
          {!isRevealed ? (
            <button onClick={handleReveal} style={actionBtnStyle}>
              Reveal
            </button>
          ) : (
            <button onClick={() => { setIsRevealed(false); setDecryptedValue(null); }} style={actionBtnStyle}>
              Hide
            </button>
          )}
          <button onClick={handleCopy} style={actionBtnStyle}>
            {copied ? 'Copied!' : 'Copy'}
          </button>
          <button onClick={handleStartEdit} style={actionBtnStyle}>
            Edit
          </button>
          <button onClick={handleShare} style={actionBtnStyle}>
            Share Link
          </button>
        </div>
      </div>

      {/* Notes */}
      {isRevealed && decryptedNotes && (
        <div
          style={{
            background: '#09090b',
            border: '1px solid #27272a',
            borderRadius: 8,
            padding: 16,
            marginBottom: 16,
          }}
        >
          <label style={{ fontSize: 12, color: '#71717a', display: 'block', marginBottom: 8 }}>
            Notes
          </label>
          <pre
            style={{
              fontFamily: 'monospace',
              fontSize: 13,
              color: '#a1a1aa',
              whiteSpace: 'pre-wrap',
              margin: 0,
            }}
          >
            {decryptedNotes}
          </pre>
        </div>
      )}

      {/* Share URL */}
      {shareUrl && (
        <div
          style={{
            background: '#172554',
            border: '1px solid #1e40af',
            borderRadius: 8,
            padding: 16,
            marginBottom: 16,
          }}
        >
          <label style={{ fontSize: 12, color: '#93c5fd', display: 'block', marginBottom: 8 }}>
            Ephemeral Share Link (expires in 24h, one view)
          </label>
          <input
            readOnly
            value={shareUrl}
            style={{
              width: '100%',
              padding: '8px 10px',
              background: '#09090b',
              border: '1px solid #27272a',
              borderRadius: 4,
              color: '#fafafa',
              fontSize: 13,
              fontFamily: 'monospace',
            }}
            onFocus={(e) => e.target.select()}
          />
          <button
            onClick={() => {
              copyToClipboard(shareUrl, 0);
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            }}
            style={{ ...actionBtnStyle, marginTop: 8 }}
          >
            {copied ? 'Copied!' : 'Copy Link'}
          </button>
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={{ color: '#f87171', fontSize: 13, marginBottom: 16 }}>{error}</div>
      )}

      {/* Delete */}
      <div style={{ borderTop: '1px solid #27272a', paddingTop: 16 }}>
        {!confirmDelete ? (
          <button
            onClick={() => setConfirmDelete(true)}
            style={{ ...actionBtnStyle, color: '#f87171', borderColor: '#f87171' + '33' }}
          >
            Delete Entry
          </button>
        ) : (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ color: '#f87171', fontSize: 13 }}>Are you sure?</span>
            <button onClick={handleDelete} style={{ ...actionBtnStyle, background: '#991b1b', color: '#fff' }}>
              Yes, delete
            </button>
            <button onClick={() => setConfirmDelete(false)} style={actionBtnStyle}>
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const backBtnStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: '#71717a',
  fontSize: 14,
  cursor: 'pointer',
  padding: 0,
};

const actionBtnStyle: React.CSSProperties = {
  padding: '6px 14px',
  background: '#27272a',
  color: '#e4e4e7',
  border: '1px solid #3f3f46',
  borderRadius: 6,
  fontSize: 13,
  cursor: 'pointer',
};

const labelStyle: React.CSSProperties = {
  fontSize: 13,
  color: '#a1a1aa',
  display: 'block',
  marginBottom: 4,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  background: '#09090b',
  border: '1px solid #27272a',
  borderRadius: 6,
  color: '#fafafa',
  fontSize: 15,
  outline: 'none',
  marginBottom: 16,
};
